import { Link } from 'react-router-dom'
import { ArrowLeft, Image } from 'lucide-react'
import * as stylex from '@stylexjs/stylex'
import { TextToImageGallery } from '@/components/gallery/TextToImageGallery'
import { ImageGalleryItem } from '@/models/gallery'
import { TextImageToImageGallery } from '@/components/gallery/TextImageToImageGallery'
import { styles } from '@/styles/ImagePage.stylex'

// Model configuration - single source of truth
const MODEL_CONFIG = {
    name: 'See Dream V4.5',
    shortName: 'See Dream',
    modelKey: 'see-dream-image',
    creditsPerImage: 4,
    heroBgImage: 'https://cdn.aibrush.co/01K9KYXE13P9VXYZ3AJFEDB90M/images/01K9RTFNJRTJJH2ZR98HTAMJ27_0_01K9RV8G5GWSM971ZPV2KA7YSE.jpg',
    providerUrl: 'https://seed.bytedance.com/en/seedream4_5',
    providerName: 'ByteDance',
    pageUrl: 'https://docs.aibrush.co/images-models/see-dream4',
} as const;

// Static gallery data - defined outside component to prevent recreation on every render
const textToImageGalleryItems: ImageGalleryItem[] = [
    {
        id: "see-dream-image-1",
        width: 1920,
        height: 1080,
        aspect_ratio: "16:9",
        src: "https://cdn.aibrush.co/01K79XMSV76PJ96SVGQ2GDD33R-workspace/images/01K8KWXP892P5B8AM2X5E2BXZX_0_01K8KX06V44W0659YZ5VA2DAS0.jpg",

        prompt: "A close-up portrait of a teenage boy with curly dark hair and freckles, wearing a navy tracksuit jacket, standing in front of a yellow wall, looking intensely into the camera with a serious expression, candid street photography style, natural emotion captured."
    },
    {
        id: "see-dream-image-2",
        width: 1920,
        height: 1080,
        aspect_ratio: "16:9",
        src: "https://cdn.aibrush.co/01K79XMSV76PJ96SVGQ2GDD33R-workspace/images/01K8KYK2SD4JDFQH1ZXA5WP1D1_0_01K8KYN8Z40BD3CBZPDGKZCFQY.jpg",
        variant: 'featured',
        prompt: "A surreal portrait of a mysterious girl with heterochromia (one blue eye, one golden eye), silver hair glowing softly in the moonlight, delicate geometric tattoos on her face, ethereal atmosphere, futuristic fashion with transparent fabrics, shimmering light particles floating around her, ultra-realistic skin texture, cinematic lighting, 8K detail, masterpiece."
    },
    {
        id: "see-dream-image-4",
        width: 1920,
        height: 1080,
        aspect_ratio: "9:16",
        src: "https://cdn.aibrush.co/01K79XMSV76PJ96SVGQ2GDD33R-workspace/images/01K8KYVN50ZV72H888NRBGYWY8_0_01K8KYXZX71Z23ZHWAFYQ1XYPC.jpg",
        variant: 'tall',
        prompt: "Close-up portrait of a snow leopard in a blizzard, fur detail visible, icy breath, 8K ultra realism, dramatic lighting."
    },
    {
        id: "see-dream-image-3",
        width: 1080,
        height: 1920,
        aspect_ratio: "9:16",
        src: "https://cdn.aibrush.co/01K79XMSV76PJ96SVGQ2GDD33R-workspace/images/01K8KXJSA7EWDVMGTVE78D3W77_0_01K8KXV2N6D87G2Y2Z05Q7GY7X.jpg",
        variant: 'tall',
        prompt: "Futuristic sneaker ad in neon blue environment with water splash motion, perfect typography for branding."

    },
    {
        id: "see-dream-image-6",
        width: 1920,
        height: 1080,
        aspect_ratio: "16:9",
        src: "https://cdn.aibrush.co/01K79XMSV76PJ96SVGQ2GDD33R-workspace/images/01K8M0D4SRCTNR068FZ9W8KNDH_0_01K8M0HA1KCZ2KES00GSV4YM2A.jpg",

        prompt: "Mercedes-Benz AMG GT, black luxury car on a wet mountain road at sunrise, golden reflections on metallic surface, cinematic lighting, light fog, ultra-realistic 8K detail, professional automotive advertisement style."
    },
    {
        id: "see-dream-image-5",
        width: 1920,
        height: 1080,
        aspect_ratio: "16:9",
        src: "https://cdn.aibrush.co/01K79XMSV76PJ96SVGQ2GDD33R-workspace/images/01K8M1FWDWQXJQ8JSZ528ZAJWS_0_01K8M1X4MBZC5PQRPJRNKMT4NW.jpg",
        variant: 'wide',
        prompt: "Pixar-style 3D fantasy character — woman with glowing teal eyes and silver hair, futuristic armor, cinematic lighting, ultra-realistic 8K animation render."
    },
    {
        id: "see-dream-image-7",
        width: 1920,
        height: 1080,
        aspect_ratio: "16:9",
        src: "https://cdn.aibrush.co/01K79XMSV76PJ96SVGQ2GDD33R-workspace/images/01K8M3X28568FACRA6Z7AZ03JW_0_01K8M3Z80T6KGMA1J2Q0E7F60B.jpg",

        prompt: "A cozy sofa with a sleeping cat and morning sunlight hitting the pillows."
    },
    {
        id: "see-dream-image-8",
        width: 1920,
        height: 1080,
        aspect_ratio: "16:9",
        src: "https://cdn.aibrush.co/01K79XMSV76PJ96SVGQ2GDD33R-workspace/images/01K8M2FE06G9R9GHMK0HFMBKXH_0_01K8M2HJFFYR8MJTHP8NERAN86.jpg",
        variant: 'featured',
        prompt: "A robot-like chair painting a canvas, splashes of color, artistic atmosphere, 8K fantasy realism.."
    },
    {
        id: "see-dream-image-9",
        width: 1080,
        height: 1920,
        aspect_ratio: "9:16",
        src: "https://cdn.aibrush.co/01K79XMSV76PJ96SVGQ2GDD33R-workspace/images/01K8M0PRJYXQV5VYVQS0DC7ZKZ_0_01K8M13NHBNBTSAJ1TG063KJSP.jpg",
        variant: 'tall',
        prompt: "Luxury beauty advertisement in the style of Fenty Beauty — confident woman holding a Fenty lipstick near her lips, glowing radiant skin, bold glossy makeup, soft colorful studio lighting, elegant clean background with negative space for logo placement, ultra-realistic 8K detail, cinematic composition, professional beauty photography."
    },
    {
        id: "see-dream-image-10",
        width: 1080,
        height: 1920,
        aspect_ratio: "9:16",
        src: "https://cdn.aibrush.co/01K79XMSV76PJ96SVGQ2GDD33R-workspace/images/01K8M2WGNWD4KQ4541WPQKEK9X_0_01K8M314F0AQGH8EETC7QESVR9.jpg",
        variant: 'tall',
        prompt: "A mysterious figure standing under a dim streetlight in the fog, wearing a long coat and a glowing mask, soft cinematic lighting, wet reflections on the ground, surreal atmosphere, ultra-realistic 8K detail, enigmatic mood."
    },
    {
        id: "see-dream-image-11",
        width: 1920,
        height: 1080,
        aspect_ratio: "16:9",
        src: "https://cdn.aibrush.co/01K79XMSV76PJ96SVGQ2GDD33R-workspace/images/01K8M3G1ZPKNDE1YKCF44XS3X9_0_01K8M3KR6B9CAT34PTVXNYE03Z.jpg",

        prompt: "A floating sphere made of fire slowly rotating in the night sky."
    },
    {
        id: "runway-image-12",
        width: 1920,
        height: 1080,
        aspect_ratio: "16:9",
        src: "https://cdn.aibrush.co/01K79XMSV76PJ96SVGQ2GDD33R-workspace/images/01K8M260Y8WHMW6VACRMCYBYN2_0_01K8M286XYBDHT416KPSE8V3CC.jpg",

        prompt: "Striking fantasy character design — mysterious humanoid with glowing eyes and elegant metallic skin, futuristic jewelry, flowing silver-blue hair, soft magical lighting, cinematic portrait, ultra-detailed textures, hyper-realistic 8K render, visually stunning and unforgettable appearance."
    },
    {
        id: "see-dream-image-13",
        width: 1920,
        height: 1080,
        aspect_ratio: "16:9",
        src: "https://cdn.aibrush.co/01K79XMSV76PJ96SVGQ2GDD33R-workspace/images/01K8M37YAXTSMD356K42E1GX5B_0_01K8M3ANNQSA05N010F7SHQPDR.jpg",

        prompt: "A glowing door floating in the middle of a dark forest, soft fog and moonlight, mysterious energy."
    },
];

const textImageToImageGalleryItems: ImageGalleryItem[] = [
    {
        id: "see-dream-image-14",
        width: 1920,
        height: 1080,
        aspect_ratio: "16:9",
        src: "https://cdn.aibrush.co/01K79XMSV76PJ96SVGQ2GDD33R-workspace/images/01K8M5NWC4N1PNJBV38ZYWVFYG_0_01K8M5RZ1PN62A2AH41PBG3472.jpg",

        prompt: "Keep everything the same but change the lighting to golden hour with soft warm tones.",
        referenceImages: ["https://cdn.aibrush.co/01K79XMSV76PJ96SVGQ2GDD33R-workspace/images/01K8M5Q49RT00BFRPCNWMYA5HY.jpeg"]
    },
    {
        id: "see-dream-image-15",
        width: 1920,
        height: 1080,
        aspect_ratio: "16:9",
        src: "https://cdn.aibrush.co/01K79XMSV76PJ96SVGQ2GDD33R-workspace/images/01K8M960REX48NM6VMY890VJZ8_0_01K8M9ARABYQ9TYHPNJRCWRTN7.jpg",

        prompt: "remove the lipstick",
        referenceImages: ["https://cdn.aibrush.co/01K79XMSV76PJ96SVGQ2GDD33R-workspace/images/01K8M0PRJYXQV5VYVQS0DC7ZKZ_0_01K8M13NHBNBTSAJ1TG063KJSP.jpg"]
    },
    {
        id: "see-dream-image-16",
        width: 1920,
        height: 1080,
        aspect_ratio: "16:9",
        src: "https://cdn.aibrush.co/01K79XMSV76PJ96SVGQ2GDD33R-workspace/images/01K8M9Q37JB41RGH7556PR381F_0_01K8MAEH664KYFDT6QK20QC01C.jpg",

        prompt: "Make the girl ride the leopard in a cold snowy environment",
        referenceImages: ["https://cdn.aibrush.co/01K79XMSV76PJ96SVGQ2GDD33R-workspace/images/01K8M960REX48NM6VMY890VJZ8_0_01K8M9ARABYQ9TYHPNJRCWRTN7.jpg", "https://cdn.aibrush.co/01K79XMSV76PJ96SVGQ2GDD33R-workspace/images/01K8KYVN50ZV72H888NRBGYWY8_0_01K8KYXZX71Z23ZHWAFYQ1XYPC.jpg"]
    },
    {
        id: "see-dream-image-17",
        width: 1920,
        height: 1080,
        aspect_ratio: "16:9",
        src: "https://cdn.aibrush.co/01K79XMSV76PJ96SVGQ2GDD33R-workspace/images/01K8M7HW6S85962PY5RR77GJV4_0_01K8M7QAJHENE2ATYNTWP5N9S9.jpg",

        prompt: "Place the cat sitting on the phone screen showing a music app. Make the cat look cool and stylish, wearing trendy sunglasses and headphones. Replace the background with a modern neon-style room or studio lighting",
        referenceImages: ["https://cdn.aibrush.co/01K79XMSV76PJ96SVGQ2GDD33R-workspace/images/01K8M7JWRFV7D3TYM33GNYZEWY.jpeg", "https://cdn.aibrush.co/01K79XMSV76PJ96SVGQ2GDD33R-workspace/images/01KA11APDRGVK8WH41PHSM6211.jpeg"]
    },
    {
        id: "see-dream-image-18",
        width: 1920,
        height: 1080,
        aspect_ratio: "16:9",
        src: "https://cdn.aibrush.co/01K79XMSV76PJ96SVGQ2GDD33R-workspace/images/01K9HCYCHBCW8HH9QDZXPDBRRV_0_01K9HEN3R9TM0QSYBEWCWAF6BH.jpg",

        prompt: "The character wore the blue sneaker, and the car in the background",
        referenceImages: ["https://cdn.aibrush.co/01K79XMSV76PJ96SVGQ2GDD33R-workspace/images/01K8M2WGNWD4KQ4541WPQKEK9X_0_01K8M314F0AQGH8EETC7QESVR9.jpg", "https://cdn.aibrush.co/01K79XMSV76PJ96SVGQ2GDD33R-workspace/images/01K8KXJSA7EWDVMGTVE78D3W77_0_01K8KXV2N6D87G2Y2Z05Q7GY7X.jpg", "https://cdn.aibrush.co/01K79XMSV76PJ96SVGQ2GDD33R-workspace/images/01K8M0D4SRCTNR068FZ9W8KNDH_0_01K8M0HA1KCZ2KES00GSV4YM2A.jpg"]
    }
];

const allTextImageToImage: string[] = [
    "https://cdn.aibrush.co/01K79XMSV76PJ96SVGQ2GDD33R-workspace/images/01K8M2WGNWD4KQ4541WPQKEK9X_0_01K8M314F0AQGH8EETC7QESVR9.jpg",
    "https://cdn.aibrush.co/01K79XMSV76PJ96SVGQ2GDD33R-workspace/images/01K8KXJSA7EWDVMGTVE78D3W77_0_01K8KXV2N6D87G2Y2Z05Q7GY7X.jpg",
    "https://cdn.aibrush.co/01K79XMSV76PJ96SVGQ2GDD33R-workspace/images/01K8M0D4SRCTNR068FZ9W8KNDH_0_01K8M0HA1KCZ2KES00GSV4YM2A.jpg",
    "https://cdn.aibrush.co/01K79XMSV76PJ96SVGQ2GDD33R-workspace/images/01K9HCYCHBCW8HH9QDZXPDBRRV_0_01K9HEN3R9TM0QSYBEWCWAF6BH.jpg",
    "https://cdn.aibrush.co/01K79XMSV76PJ96SVGQ2GDD33R-workspace/images/01K8M7JWRFV7D3TYM33GNYZEWY.jpeg",
    "https://cdn.aibrush.co/01K79XMSV76PJ96SVGQ2GDD33R-workspace/images/01KA11APDRGVK8WH41PHSM6211.jpeg",
    "https://cdn.aibrush.co/01K79XMSV76PJ96SVGQ2GDD33R-workspace/images/01K8M7HW6S85962PY5RR77GJV4_0_01K8M7QAJHENE2ATYNTWP5N9S9.jpg",
    "https://cdn.aibrush.co/01K79XMSV76PJ96SVGQ2GDD33R-workspace/images/01K8M960REX48NM6VMY890VJZ8_0_01K8M9ARABYQ9TYHPNJRCWRTN7.jpg",
    "https://cdn.aibrush.co/01K79XMSV76PJ96SVGQ2GDD33R-workspace/images/01K8KYVN50ZV72H888NRBGYWY8_0_01K8KYXZX71Z23ZHWAFYQ1XYPC.jpg",
    "https://cdn.aibrush.co/01K79XMSV76PJ96SVGQ2GDD33R-workspace/images/01K8M9Q37JB41RGH7556PR381F_0_01K8MAEH664KYFDT6QK20QC01C.jpg",
    "https://cdn.aibrush.co/01K79XMSV76PJ96SVGQ2GDD33R-workspace/images/01K8M0PRJYXQV5VYVQS0DC7ZKZ_0_01K8M13NHBNBTSAJ1TG063KJSP.jpg",
    "https://cdn.aibrush.co/01K79XMSV76PJ96SVGQ2GDD33R-workspace/images/01K8M960REX48NM6VMY890VJZ8_0_01K8M9ARABYQ9TYHPNJRCWRTN7.jpg",
    "https://cdn.aibrush.co/01K79XMSV76PJ96SVGQ2GDD33R-workspace/images/01K8M5Q49RT00BFRPCNWMYA5HY.jpeg",
    "https://cdn.aibrush.co/01K79XMSV76PJ96SVGQ2GDD33R-workspace/images/01K8M5NWC4N1PNJBV38ZYWVFYG_0_01K8M5RZ1PN62A2AH41PBG3472.jpg"
];

export default function SeeDreamPage() {
    return (
        <div {...stylex.props(styles.modelPage)}>
            <Link to="/" {...stylex.props(styles.backLink)}>
                <ArrowLeft size={20} />
                Back to Home
            </Link>

            {/* Hero Section */}
            <section {...stylex.props(styles.heroSection)}>
                <div {...stylex.props(styles.heroBackground)}>
                    <img
                        src={MODEL_CONFIG.heroBgImage}
                        {...stylex.props(styles.heroBgImage)}
                        alt={`${MODEL_CONFIG.name} hero image`}
                    />
                    <div {...stylex.props(styles.heroOverlay)}></div>
                </div>
                <div {...stylex.props(styles.heroContent)}>
                    <h2 {...stylex.props(styles.heroTitle)}>{MODEL_CONFIG.name.toUpperCase()}</h2>
                    <p {...stylex.props(styles.heroDescription)}>
                        Bring any <strong>concept to life in stunning detail</strong> from <strong>Text to Image</strong> and <strong>Text & Image to Image</strong> with multi-reference support for consistent, impressive results.
                        <br />
                        Powered by <a href={MODEL_CONFIG.providerUrl} target="_blank" rel="noopener noreferrer">{MODEL_CONFIG.providerName}</a>
                    </p>
                    <div {...stylex.props(styles.heroStats)}>
                        <div {...stylex.props(styles.stat)}>
                            <Image size={20} />
                            <span>Text to Image</span>
                        </div>
                        <div {...stylex.props(styles.stat)}>
                            <Image size={20} />
                            <span>Text & Image to Image</span>
                        </div>
                        <div {...stylex.props(styles.stat)}>
                            <Image size={20} />
                            <span>Up to 10 reference images - endless creativity</span>
                        </div>
                    </div>
                    <div {...stylex.props(styles.creditCostBadge)}>{MODEL_CONFIG.creditsPerImage} credits per image</div>
                </div>
            </section>

            {/* Image Gallery */}
            <TextToImageGallery
                modelKey={MODEL_CONFIG.modelKey}
                modelLabel={MODEL_CONFIG.shortName}
                modelNote={`This model costs <strong>${MODEL_CONFIG.creditsPerImage} credits</strong> per image.`}
                title="Text to Image Gallery"
                description={`Explore the exceptional capabilities of <a href='${MODEL_CONFIG.providerUrl}' target='_blank' rel='noopener noreferrer'>${MODEL_CONFIG.name}</a> through these AI-generated images. <strong>Click on any image</strong> to see how it was created.`}
                items={textToImageGalleryItems} />

            {/* Text & Image to Image Gallery */}
            <TextImageToImageGallery
                modelKey={MODEL_CONFIG.modelKey}
                modelLabel={MODEL_CONFIG.shortName}
                modelNote={`This model costs <strong>${MODEL_CONFIG.creditsPerImage} credits</strong> per image.`}
                title="Text & Image to Image Gallery"
                description={`Experience advanced image editing with <a href='${MODEL_CONFIG.providerUrl}' target='_blank' rel='noopener noreferrer'>${MODEL_CONFIG.name}</a>. Effortlessly <strong>combine multiple references</strong> can reference up to <strong>10 images simultaneously</strong> and <strong>adjust environments & modify lighting </strong> ... <strong>Click on any image</strong> to see how it was created.`}
                allImages={allTextImageToImage}
                items={textImageToImageGalleryItems} />
        </div>
    )
}
